import type { ApiResponse, PaginationMeta } from './axios';

export class ApiContractError extends Error {
  readonly code = 'API_CONTRACT_ERROR';
  constructor(context: string, expected: string) {
    super(`تعذر قراءة بيانات الخادم (${context}): expected ${expected}`);
    this.name = 'ApiContractError';
  }
}

export class UnsupportedApiError extends Error {
  readonly code = 'UNSUPPORTED_API';
  constructor(feature: string) {
    super(`هذه العملية غير متاحة في الخادم الحالي: ${feature}`);
    this.name = 'UnsupportedApiError';
  }
}

export function requireRecord(value: unknown, context: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new ApiContractError(context, 'object');
  }
  return value as Record<string, unknown>;
}

export function requireArray<T>(value: unknown, context: string): T[] {
  if (!Array.isArray(value)) throw new ApiContractError(context, 'array');
  return value as T[];
}

// Missing optional collections are empty; a present value of the wrong type is a bug.
export function optionalArray<T>(value: unknown, context = 'optional collection'): T[] {
  return value == null ? [] : requireArray<T>(value, context);
}

export function unwrapResponse<T>(value: unknown, context: string): T {
  const envelope = requireRecord(value, context);
  if (envelope.success !== true || !('data' in envelope)) {
    throw new ApiContractError(context, '{ success: true, data }');
  }
  return envelope.data as T;
}

export interface ListResult<T> extends PaginationMeta {
  items: T[];
  paginationMode: 'client' | 'server';
}

export function normalizeListResult<T>(
  response: ApiResponse<unknown>, context: string,
  adapt: (value: unknown, context: string) => T = value => value as T,
): ListResult<T> {
  const values = requireArray<unknown>(unwrapResponse(response, context), context);
  const items = values.map((value, index) => adapt(value, `${context}[${index}]`));
  if (response.pagination) {
    const pagination = requireRecord(response.pagination, `${context}.pagination`);
    for (const key of ['page', 'limit', 'total', 'totalPages']) {
      const value = pagination[key];
      if (typeof value !== 'number' || !Number.isInteger(value) || value < (key === 'page' || key === 'limit' ? 1 : 0)) {
        throw new ApiContractError(`${context}.pagination.${key}`, 'non-negative integer');
      }
    }
    return { items, ...response.pagination, paginationMode: 'server' };
  }
  return { items, total: items.length, page: 1, limit: items.length, totalPages: items.length ? 1 : 0, paginationMode: 'client' };
}

export interface ClientListFilters {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// Only paginate arrays returned in full. Server pagination is preserved verbatim.
export function selectListPage<T>(result: ListResult<T> | undefined, filters: ClientListFilters): ListResult<T> {
  const limit = Math.max(1, filters.limit ?? result?.items.length ?? 10);
  if (!result) return { items: [], total: 0, page: 1, limit, totalPages: 0, paginationMode: 'client' };
  if (result.paginationMode === 'server') return result;
  const items = [...result.items];
  if (filters.sortBy) {
    const field = filters.sortBy;
    items.sort((a, b) => {
      const left = requireRecord(a, 'list item')[field];
      const right = requireRecord(b, 'list item')[field];
      const difference = typeof left === 'number' && typeof right === 'number'
        ? left - right : String(left ?? '').localeCompare(String(right ?? ''), 'ar', { numeric: true });
      return filters.sortOrder === 'desc' ? -difference : difference;
    });
  }
  const totalPages = Math.ceil(items.length / limit);
  const page = Math.max(1, Math.min(filters.page ?? 1, totalPages || 1));
  return { ...result, items: items.slice((page - 1) * limit, page * limit), page, limit, totalPages };
}

export function normalizeNamedRecord<T>(value: unknown, context: string): T {
  const record = requireRecord(value, context);
  if (typeof record.id !== 'string' || typeof record.name !== 'string') throw new ApiContractError(context, 'id and name strings');
  return record as T;
}

export function normalizeEntity<T>(value: unknown, context: string, numbers: string[] = []): T {
  const record = normalizeNamedRecord<Record<string, unknown>>(value, context);
  if (typeof record.isActive !== 'boolean') throw new ApiContractError(`${context}.isActive`, 'boolean');
  if (typeof record.createdAt !== 'string' || !Number.isFinite(Date.parse(record.createdAt))) throw new ApiContractError(`${context}.createdAt`, 'ISO date string');
  return normalizeNumbers<T>(record, context, numbers);
}

export function normalizeCollections<T>(value: unknown, context: string, keys: string[]): T {
  const record = { ...requireRecord(value, context) };
  for (const key of keys) record[key] = optionalArray(record[key], `${context}.${key}`);
  return record as T;
}

export function requireNumber(value: unknown, context: string): number {
  const number = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  if (typeof number !== 'number' || !Number.isFinite(number)) throw new ApiContractError(context, 'finite number');
  return number;
}

export function normalizeNumbers<T>(value: unknown, context: string, keys: string[]): T {
  const record = { ...requireRecord(value, context) };
  for (const key of keys) record[key] = requireNumber(record[key], `${context}.${key}`);
  return record as T;
}
